import { create } from 'zustand';
import type { SortDirection } from '@staff-app/shared-types';
import { SORT_DIRECTION } from '@staff-app/shared-types';

/**
 * Sort field = a device-table column id. The staff devices hook fetches every
 * device and sorts client-side (see useStaffCustomerDevices), so EVERY column is
 * sortable and the keys never reach the backend — they don't have to match the
 * backend `DeviceSort` input. Do NOT forward these to a server-side `devices()`
 * query (the apps/web copy, which sorts server-side, must keep a narrower set).
 */
export type MdmDeviceSortField =
  | 'status'
  | 'deviceName'
  | 'fleet'
  | 'team'
  | 'path'
  | 'lastHeartBeat'
  | 'model'
  | 'os'
  | 'osVersion'
  | 'platform'
  | 'localAppVersion'
  | 'assignType'
  | 'assignName'
  | 'assignId'
  | 'vpnIp'
  | 'serialNo'
  | 'UUID'
  | 'tags'
  | 'tz'
  | 'activationDate';

interface MdmDevicesSortState {
  sortField: MdmDeviceSortField;
  sortDirection: SortDirection;
  setSort: (field: MdmDeviceSortField, direction: SortDirection) => void;
}

const STORAGE_KEY = 'scio3-staff-mdm-devices-sorting';

const ALLOWED_FIELDS: readonly MdmDeviceSortField[] = [
  'status',
  'deviceName',
  'fleet',
  'team',
  'path',
  'lastHeartBeat',
  'model',
  'os',
  'osVersion',
  'platform',
  'localAppVersion',
  'assignType',
  'assignName',
  'assignId',
  'vpnIp',
  'serialNo',
  'UUID',
  'tags',
  'tz',
  'activationDate',
];

function loadSort(): { sortField: MdmDeviceSortField; sortDirection: SortDirection } {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<MdmDevicesSortState>;
      // Migrate stale values (e.g. 'lastHeartBeat' from an earlier build) to
      // a backend-accepted default. Prevents the list from silently going
      // empty after the sort contract was narrowed.
      const field =
        parsed.sortField && ALLOWED_FIELDS.includes(parsed.sortField as MdmDeviceSortField)
          ? (parsed.sortField as MdmDeviceSortField)
          : 'activationDate';
      return {
        sortField: field,
        sortDirection: parsed.sortDirection ?? SORT_DIRECTION.DESC,
      };
    }
  } catch {
    // ignore parse errors — fall through to defaults
  }
  return { sortField: 'activationDate', sortDirection: SORT_DIRECTION.DESC };
}

const initial = loadSort();

export const useMdmDevicesSortStore = create<MdmDevicesSortState>((set) => ({
  sortField: initial.sortField,
  sortDirection: initial.sortDirection,
  setSort: (sortField, sortDirection) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ sortField, sortDirection }));
    } catch {
      // storage quota / private mode — ignore
    }
    set({ sortField, sortDirection });
  },
}));
